import { JButton } from '@atoms/JButton';
import Jpaper from '@atoms/JPaper';
import { IconSize } from '@constants';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import useUserMutationContext from '../../hooks/useUserMutationContext';
import UserFormDetailsData from '../../models/user-form-details-data';
import UserFormRolesData from '../../models/user-form-roles-data';
import {
  useCreateUserRequest,
  useEditUserRequest,
} from '../../mutations/userMutations';
import { useEffect, useState } from 'react';

import Paths from '@/routes/paths';
import { useNavigate, useParams } from 'react-router-dom';
import useJSnackbar from '@/hooks/useJSnackbar';
import { Loading } from '@atoms/Loading';

type valuesProp = UserFormDetailsData | UserFormRolesData;

interface Props {
  isValid: boolean;
  values: valuesProp;
  submitTab?: boolean;
}

const UserFormFooter = ({ isValid, values, submitTab }: Props) => {
  const { t } = useTranslation();
  const [submit, setSubmit] = useState(false);
  const navigation = useNavigate();
  const { id } = useParams();
  const { setStepIndex, stepIndex, setPayload, payload, mode } =
    useUserMutationContext();
  const {
    mutate: createUser,
    isSuccess: isCreateSuccess,
    isLoading: isCreateLoading,
    isError: isCreateError,
  } = useCreateUserRequest();
  const {
    mutate: editUser,
    isSuccess: isEditSuccess,
    isLoading: isEditLoading,
    isError: isEditError,
  } = useEditUserRequest();
  const { showSnackbar } = useJSnackbar();

  const setDtoValues = async (data: valuesProp) => {
    if ('firstname' in data) {
      setPayload((prev) => {
        return {
          userDto: data,
          userRolesEstablishmentDto: prev?.userRolesEstablishmentDto,
          userRolesEnterpriseDto: prev?.userRolesEnterpriseDto,
        };
      });
    }

    if ('userRolesEstablishment' in data && data.userRolesEstablishment) {
      setPayload((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          userRolesEstablishmentDto: data.userRolesEstablishment,
        };
      });
    }
    if ('userRolesEnterprise' in data && data.userRolesEnterprise) {
      setPayload((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          userRolesEnterpriseDto: data.userRolesEnterprise,
        };
      });
    }
  };

  const handleSubmit = () => {
    if (!payload) return;
    if (payload.userRolesEnterpriseDto) {
      if (mode === 'create') createUser({ payload, userType: 'enterprise' });
      else if (mode === 'edit')
        editUser({ payload, userType: 'enterprise', id });
    }
    if (
      payload.userRolesEstablishmentDto &&
      payload.userRolesEstablishmentDto.length > 0
    )
      if (mode === 'create') createUser({ payload, userType: 'establishment' });
      else if (mode === 'edit')
        editUser({
          payload,
          userType: 'establishment',
          id,
        });
  };
  const onNextClick = (formValues: valuesProp) => {
    setDtoValues(formValues);
    if (!submitTab) {
      setStepIndex((prev) => (prev ? prev + 1 : 1));
      return;
    }
    setSubmit(true);
  };
  const onPreviousClick = (formValues: valuesProp) => {
    if (stepIndex === 0) return;
    setDtoValues(formValues);
    setStepIndex((prev) => (prev ? prev - 1 : 1));
  };

  useEffect(() => {
    if (!submit || !payload || !submitTab) return;
    handleSubmit();
    setSubmit(false);
  }, [submit, payload, submitTab]);

  useEffect(() => {
    if (!isCreateSuccess) return;
    navigation(Paths.UsersPage);
    showSnackbar('Utilisateur créé avec succès', 'success');
  }, [isCreateSuccess]);
  useEffect(() => {
    if (!isEditSuccess) return;
    navigation(Paths.UsersPage);
    showSnackbar("L'utilisateur a été mis à jour avec succès", 'success');
  }, [isEditSuccess]);

  useEffect(() => {
    if (isCreateError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isCreateError]);

  useEffect(() => {
    if (isEditError) {
      showSnackbar(t('API_ERROR'), 'error');
      setSubmit(false);
    }
  }, [isEditError]);

  if (isCreateLoading || isEditLoading) return <Loading />;
  return (
    <Jpaper direction="row" justifyContent="space-between" alignItems="center">
      <JButton
        disabled={stepIndex === 0}
        onClick={() => onPreviousClick(values)}
        startIcon={<ArrowLeftIcon height={IconSize.md} />}
        variant="text"
      >
        {t('PREVIOUS')}
      </JButton>
      <JButton
        type="button"
        disabled={!isValid}
        onClick={() => onNextClick(values)}
        endIcon={<ArrowRightIcon height={IconSize.md} />}
        variant="contained"
      >
        {submitTab && mode === 'create'
          ? t('CREATE_USER')
          : submitTab && mode === 'edit'
          ? t('EDIT_USER')
          : t('NEXT')}
      </JButton>
    </Jpaper>
  );
};

export default UserFormFooter;
